import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  // Load cart from localStorage safely
  const [cart, setCart] = useState(() => {
    try {
      const stored = localStorage.getItem("cart");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [showCart, setShowCart] = useState(false);

  // Persist cart to browser storage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // Add to cart with direct Cloudinary resolution
  const addToCart = (product) => {
    if (!product?.id) return;

    setCart((prev) => {
      const exists = prev.find((item) => item.id === product.id);

      if (exists) {
        return prev.map((item) =>
          item.id === product.id ? { ...item, qty: item.qty + 1 } : item
        );
      }

      // ✅ CLOUDINARY RESOLUTION: Handles nested objects or direct strings
      const cloudImage = 
        product.image?.url || 
        product.image?.secure_url || 
        (Array.isArray(product.image) ? product.image[0] : product.image) ||
        product.imageUrl || 
        null;

      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          price: Number(product.price),
          qty: 1,
          image: cloudImage, // Stores the direct link to Cloudinary
        },
      ];
    });

    setShowCart(true);
  };

  const increaseQty = (id) => {
    setCart((prev) =>
      prev.map((item) => (item.id === id ? { ...item, qty: item.qty + 1 } : item))
    );
  };

  const decreaseQty = (id) => {
    setCart((prev) =>
      prev
        .map((item) => (item.id === id ? { ...item, qty: item.qty - 1 } : item))
        .filter((item) => item.qty > 0)
    );
  };

  const removeItem = (id) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  const total = cart.reduce((sum, item) => sum + Number(item.price) * item.qty, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, increaseQty, decreaseQty, removeItem, showCart, setShowCart, total }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);